import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ListFilter as FilterIcon, X as XIcon } from "lucide-react";
import type {
  ConversationListContactStateName,
  ConversationListSortName,
  ConversationListStatusName,
  PlatformTypeName,
} from "@multi-chat/shared";
import { Button } from "../../../components/ui/button/index.js";
import { Sheet, SheetClose, SheetContent, SheetTitle } from "../../../components/ui/sheet/index.js";
import {
  defaultMobileFilterSortDraft,
  listFilterButtonCount,
  mobileFilterSortDraftFromQuery,
  type MobileFilterSortDraft,
} from "../conversation-list-filters.js";
import { InboxIconButton, InboxQuickButton, InboxQuickCount } from "../InboxIconButton.js";
import { ConversationFilterSections } from "./ConversationFilterSections.js";

export interface MobileFiltersSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platform?: PlatformTypeName;
  unread: boolean;
  contactState?: ConversationListContactStateName;
  status: ConversationListStatusName;
  sort: ConversationListSortName;
  onApply: (draft: MobileFilterSortDraft) => void;
}

/** Mobile filters keep a draft and apply it together. */
export function MobileFiltersSheet({
  open,
  onOpenChange,
  platform,
  unread,
  contactState,
  status,
  sort,
  onApply,
}: MobileFiltersSheetProps): React.JSX.Element {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<MobileFilterSortDraft>(() =>
    mobileFilterSortDraftFromQuery({ platform, unread, contactState, status, sort }),
  );

  function handleOpen(nextOpen: boolean): void {
    if (nextOpen) {
      setDraft(mobileFilterSortDraftFromQuery({ platform, unread, contactState, status, sort }));
    }
    onOpenChange(nextOpen);
  }

  function handleApply(): void {
    onApply(draft);
    onOpenChange(false);
  }

  function handleClear(): void {
    setDraft(defaultMobileFilterSortDraft());
  }

  return (
    <Sheet open={open} onOpenChange={handleOpen}>
      <SheetContent
        side="bottom"
        className="font-feedbot flex max-h-[86dvh] flex-col gap-0 overflow-hidden rounded-t-[18px] border-0 bg-surface p-0 text-inbox-text shadow-[0_-12px_40px_rgb(6_29_26/0.2)]"
        overlayClassName="bg-[rgb(6_29_26/0.35)]"
        aria-describedby={undefined}
      >
        <span className="mx-auto mt-2 mb-0.5 block h-1 w-10 shrink-0 rounded-full bg-[#d6dfdc]" aria-hidden="true" />
        <div className="flex shrink-0 items-center px-3.5 pt-1.5 pb-2">
          <SheetTitle className="m-0 text-base font-bold text-inbox-strong">
            {t("inbox.mobile.sheetTitle")}
          </SheetTitle>
          <SheetClose asChild>
            <InboxIconButton className="ms-auto" aria-label={t("inbox.filters.close")}>
              <XIcon size={18} aria-hidden="true" />
            </InboxIconButton>
          </SheetClose>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <ConversationFilterSections value={draft} onChange={setDraft} />
        </div>
        <div className="flex shrink-0 items-center gap-2 border-t border-inbox-line-soft bg-[#fafcfb] px-3.5 py-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom,0px))]">
          <Button
            type="button"
            variant="outline"
            className="h-[42px] flex-1 rounded-[9px] border-[#d6e2dc] text-[0.8125rem] text-[#2d4a42]"
            onClick={handleClear}
          >
            {t("inbox.filters.reset")}
          </Button>
          <Button
            type="button"
            className="h-[42px] flex-1 rounded-[9px] text-[0.8125rem]"
            onClick={handleApply}
          >
            {t("inbox.mobile.apply")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** Opens the mobile filter sheet; the badge counts channel and contact like the desktop popover. */
export function MobileFiltersTrigger({
  platform,
  contactState,
  onClick,
}: {
  platform?: PlatformTypeName;
  contactState?: ConversationListContactStateName;
  onClick: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const count = listFilterButtonCount({ platform, contactState });
  const label = count > 0 ? t("inbox.filters.buttonActive", { count }) : t("inbox.mobile.filtersAndSort");

  return (
    <InboxQuickButton onClick={onClick} active={count > 0} aria-haspopup="dialog" aria-label={label}>
      <FilterIcon size={16} aria-hidden="true" />
      <InboxQuickCount count={count} />
    </InboxQuickButton>
  );
}
